// @flow

/**
 * Lo que comparten todas las landings de campaña de Finect Plus. Lo propio de cada promoción
 * —código, porcentaje, fechas, paleta, copys— vive en su ficha de `campaigns/`.
 */

/**
 * Ficha de una campaña: los datos con los que la web decide si está viva. Su maqueta y sus textos
 * están en `views/`, que se descargan aparte (ver views/index.js).
 */
export type PromoCampaign = {
  /** Valor de `?promo=` con el que se pide la landing, en minúsculas; también la clave de su vista */
  id: string,
  /** Código promocional, tal como está en Stripe */
  code: string,
  /** Porcentaje de descuento */
  discount: number,
  /** Primer día de campaña (YYYY-MM-DD), o null si no hay que esperar a ninguno */
  startIso: ?string,
  /** Último día de campaña (YYYY-MM-DD) si el ajuste del backoffice no dice otra cosa */
  endIso: string,
  /** Clave del ajuste de dashboard2 que enciende, apaga o prorroga la campaña */
  setting: string,
  /** Identidad para GA4 */
  ga4: { promotionName: string, label: string, pageId: string },
  /** Clave de su maqueta en VIEWS si no es su `id` (las de influencer comparten una) */
  view?: string,
  dynamic?: false
};

/**
 * Ficha de una landing de influencer: lo único que se sabe con la URL (`?promo=<slug>`). El código, el
 * descuento y las fechas no están aquí, porque no son de código: los da de alta Marketing en dashboard2,
 * en el ajuste `plus-influencer-<slug>`, y usePromoStatus los junta con esto al leerlo (ver
 * influencerSetting.js). Igual que las fichas estáticas, viaja en el bundle principal: solo datos.
 */
export type InfluencerCampaign = {
  /** El slug: valor de `?promo=`, en minúsculas */
  id: string,
  dynamic: true,
  /** Clave de su maqueta en VIEWS */
  view: string,
  /** Clave del ajuste con la ficha del influencer */
  setting: string,
  /** Identidad para GA4. `promotionName` lleva el año, que solo dice el ajuste */
  ga4: { label: string, pageId: string },
  /** Si la URL pide la vista previa (`?preview=1`). Solo cuenta en pre-release: ver getPromoStatus */
  preview: boolean
};

/** Lo que puede pedir una URL con `?promo=`: una campaña de código o la landing de un influencer */
export type PromoRequest = PromoCampaign | InfluencerCampaign;

/**
 * Ficha de un influencer tal como la guarda dashboard2 en `plus-influencer-<slug>`, ya validada por
 * parseInfluencerSetting: lo que no viene o no se entiende llega vacío (texto por defecto).
 *
 * Se lee por la API pública de ajustes: aquí no puede ir nada privado, ni condiciones económicas ni
 * contacto.
 */
export type InfluencerSetting = {
  /** Solo `true` la enciende: la ficha nace apagada */
  active: boolean,
  name: string,
  /** Código promocional, tal como está en Stripe */
  code: string,
  /** Porcentaje, copiado del cupón al comprobarlo en el dashboard (la web no puede leer Stripe) */
  discount: number,
  /** Primer día (YYYY-MM-DD), o null si sale en cuanto se enciende */
  startDate: ?string,
  /** Último día, incluido (YYYY-MM-DD) */
  endDate: string,
  /** Año de la campaña, para `promotion_name` */
  year: number,
  /** Id de la foto en el CDN de imágenes, sin sufijos, o null */
  photo: ?string,
  social: ?('instagram' | 'youtube' | 'tiktok' | 'x'),
  handle: string,
  quote: string,
  texts: { ticker: string, eyebrow: string, codeLabel: string, cta: string, bannerEyebrow: string },
  faq: { question: string, answer: string },
  seo: { title: string, description: string }
};

/**
 * La landing no tiene URL propia: vive en `/servicios/finect-plus/comprar?promo=<campaña>`, la
 * página de compra de siempre con un parámetro. Esa es la URL a la que apuntan las campañas.
 *
 * Así hereda del checkout el shell de la app (OmniNav y footer de Finect), las tarifas y los
 * guards que ya existen —entre ellos el que manda a su panel a quien ya es Plus—, en vez de
 * tener que replicarlos.
 */
export const PLUS_CHECKOUT_PATH = '/servicios/finect-plus/comprar';
export const PROMO_PARAM = 'promo';
export const PREVIEW_PARAM = 'preview';

// Landings de influencer: `?promo=<slug>` con un slug que no es de ninguna campaña de código. El slug
// lo fija Marketing al crearla y no cambia (es lo que el influencer publica en su bio); solo los que
// cumplen el formato cuestan una petición del ajuste.
export const INFLUENCER_SLUG = /^[a-z0-9-]{3,40}$/;
export const INFLUENCER_SETTING_PREFIX = 'plus-influencer-';
export const INFLUENCER_VIEW = 'influencer';

// Las campañas vencen a medianoche aquí, no en el huso de quien mira. Fijarlo es lo que hace que el
// servidor y el navegador decidan lo mismo (ver usePromoStatus).
export const PROMO_TIMEZONE = 'Europe/Madrid';

// Landing de campaña: no se indexa, igual que la página de compra sobre la que se monta.
export const PROMO_ROBOTS = 'noindex, follow';

/**
 * La URL de la landing de una campaña, que es a la que apuntan sus anuncios, emails y banners.
 *
 * @param campaign Ficha de la campaña
 */
export const getLandingUrl = (campaign: PromoRequest): string => `${PLUS_CHECKOUT_PATH}?${PROMO_PARAM}=${campaign.id}`;

const MONTHS = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre'
];

const pad = (value: number): string => String(value).padStart(2, '0');

/**
 * Los formatos de copy de la fecha de fin, derivados de una sola fecha.
 *
 * Los meses van escritos y no salen de `toLocaleDateString` porque esta página se pinta en el
 * servidor y se hidrata en el navegador: dos ICU distintos darían dos textos distintos y React
 * avisaría de la discrepancia.
 *
 * @param iso Último día de campaña, YYYY-MM-DD
 */
export const getPromoDates = (iso: string): Object => {
  const [year, month, day] = iso.split('-').map(Number);
  const after = new Date(year, month - 1, day + 1);
  const afterShort = `${after.getDate()} de ${MONTHS[after.getMonth()]}`;

  return {
    long: `${day} de ${MONTHS[month - 1]} de ${year}`,
    short: `${day} de ${MONTHS[month - 1]}`,
    // «30/11», como lo escribe el aviso de vigencia de BlackPlus.
    shortNumeric: `${pad(day)}/${pad(month)}`,
    legal: `${pad(day)}/${pad(month)}/${year}`,
    // El día siguiente, que es como se cuenta la vuelta al precio habitual.
    afterShort,
    afterLong: `${afterShort} de ${after.getFullYear()}`
  };
};
